import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Zap, TrendingUp, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PricingPage = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const packages = [
    {
      name: "STARTER",
      subtitle: "LAUNCHPAD",
      price: "400",
      pages: "1-2 PAGES",
      popular: true,
      description: "Ideal for solopreneurs, tradespeople and micro-businesses who need a simple professional presence online.",
      firstDraft: "24-48 hours",
      icon: Zap,
      examples: ["Villa Kephala", "The Old Pub"],
      exampleUrls: ["https://villakephala.com", "https://www.theoldpubwiltshire.co.uk/"],
      features: [
        "1-2 custom responsive pages",
        "Contact form",
        "Google Maps embed",
        "Social media links & icons",
        "Shopify store link",
        "Google Analytics setup",
        "SSL certificate (HTTPS)",
        "Custom domain setup",
        "UK-based hosting"
      ],
      hosting: { monthly: 20, yearly: 200, saving: 40 }
    },
    {
      name: "GROWTH",
      subtitle: "MOMENTUM",
      price: "700",
      pages: "3-5 PAGES",
      popular: false,
      description: "For established small businesses who want a site that does more than just exist. Custom-designed pages with blog functionality and analytics.",
      firstDraft: "1 week",
      icon: TrendingUp,
      examples: ["Ridgeway Prints", "Kaftan Queens"],
      exampleUrls: ["https://uk-print-pro.preview.emergentagent.com", "https://kaftanqueens.co.uk/"],
      features: [
        "3-5 custom-designed pages",
        "Stripe or PayPal payments",
        "Booking/appointment system",
        "Shopify store",
        "Social media feed embed",
        "Image gallery or portfolio",
        "Contact form",
        "Google Maps embed",
        "Social media links & icons",
        "Shopify store link",
        "Google Analytics setup",
        "SSL certificate (HTTPS)",
        "Custom domain setup",
        "UK-based hosting"
      ],
      hosting: { monthly: 30, yearly: 300, saving: 60 }
    },
    {
      name: "PREMIUM",
      subtitle: "SCALE",
      price: "1,000",
      pages: "6-10 PAGES",
      popular: false,
      description: "A complete, professional online presence on a CMS you can edit yourself. Includes lead capture and client training.",
      firstDraft: "2 weeks",
      icon: Crown,
      examples: ["Safari Star"],
      exampleUrls: ["https://safari-star-preview.preview.emergentagent.com"],
      features: [
        "6-10 custom-designed pages",
        "Stripe or PayPal payments",
        "Booking/appointment system",
        "Live chat widget",
        "Shopify store",
        "Social media feed embed",
        "Image gallery or portfolio",
        "Contact form",
        "Google Maps embed",
        "Social media links & icons",
        "Shopify store link",
        "Custom domain setup",
        "UK-based hosting"
      ],
      hosting: { monthly: 50, yearly: 500, saving: 100 }
    }
  ];

  const extras = [
    { name: "AI chatbot integration", price: "150" },
    { name: "Multi-language site", price: "200" },
    { name: "Membership or login area", price: "150" },
    { name: "Online booking with payment", price: "200" },
    { name: "Custom animations", price: "100" },
    { name: "API integrations", price: "150" }
  ];

  const everyBuildIncludes = [
    "Mobile-responsive design",
    "Custom domain setup",
    "Modern, professional design",
    "SSL certificate (HTTPS)",
    "Fast UK-based hosting",
    "Ongoing technical support"
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-[#262626]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-['Oswald'] text-xl md:text-2xl font-bold tracking-tight">
            WILTSHIRE<span className="text-[#FF4500]">WEB</span><span className="text-[#888888] font-normal ml-1">STUDIO</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#888888] hover:text-white">
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <p className="font-['Manrope'] text-[#FF4500] uppercase tracking-[0.3em] text-sm mb-4">
            Pricing Guide 2026
          </p>
          <h1 className="font-['Oswald'] text-4xl md:text-6xl text-white uppercase mb-6">
            TRANSPARENT PRICING
          </h1>
          <p className="font-['Manrope'] text-[#888888] text-lg mb-4">
            Straightforward pricing – no hidden fees. All prices include VAT where applicable.
          </p>
          <p className="font-['Manrope'] text-[#FF4500] text-lg font-semibold">
            First draft delivered in as little as 24 hours
          </p>
        </motion.div>
      </section>

      {/* Packages */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Oswald'] text-3xl text-white uppercase text-center mb-12">
            Website Build Packages
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-[#0A0A0A] border ${pkg.popular ? 'border-[#FF4500]' : 'border-[#262626]'} p-8`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF4500] text-white px-4 py-1 text-xs font-['Oswald'] uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <pkg.icon className="mx-auto text-[#FF4500] mb-4" size={40} strokeWidth={1.5} />
                  <h3 className="font-['Oswald'] text-2xl text-white uppercase">{pkg.name}</h3>
                  <p className="font-['Manrope'] text-[#FF4500] text-sm uppercase tracking-wider">{pkg.subtitle}</p>
                  <p className="font-['Manrope'] text-[#888888] text-sm mt-2">{pkg.pages}</p>
                </div>
                
                <div className="text-center mb-6">
                  <span className="font-['Oswald'] text-5xl text-white">£{pkg.price}</span>
                </div>
                
                <p className="font-['Manrope'] text-[#888888] text-sm text-center mb-4">
                  {pkg.description}
                </p>

                {/* Example websites */}
                <div className="bg-[#050505] p-3 mb-4 text-center">
                  <p className="font-['Manrope'] text-[#888888] text-xs uppercase tracking-wider mb-1">Examples</p>
                  <p className="font-['Manrope'] text-sm font-semibold">
                    {pkg.examples.map((example, i) => (
                      <span key={i}>
                        {i > 0 && <span className="text-[#888888]"> • </span>}
                        <a 
                          href={pkg.exampleUrls[i]} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#FF4500] hover:underline"
                        >
                          {example}
                        </a>
                      </span>
                    ))}
                  </p>
                </div>
                
                <div className="bg-[#050505] p-4 mb-6 text-center">
                  <p className="font-['Manrope'] text-[#FF4500] text-sm">
                    First draft: <span className="text-white font-semibold">{pkg.firstDraft}</span>
                  </p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-[#FF4500] mt-1 flex-shrink-0" />
                      <span className="font-['Manrope'] text-[#CCCCCC] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="border-t border-[#262626] pt-6">
                  <p className="font-['Manrope'] text-[#888888] text-xs uppercase tracking-wider mb-2">
                    Hosting & Maintenance
                  </p>
                  <p className="font-['Manrope'] text-white">
                    £{pkg.hosting.monthly}/mo <span className="text-[#888888]">or</span> £{pkg.hosting.yearly}/yr 
                    <span className="text-[#FF4500] text-sm ml-2">(save £{pkg.hosting.saving})</span>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Every Build Includes */}
      <section className="py-16 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Oswald'] text-3xl text-white uppercase mb-8">
            Every Build Includes
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {everyBuildIncludes.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check size={20} className="text-[#FF4500]" />
                <span className="font-['Manrope'] text-[#CCCCCC]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Changes & Updates */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['Oswald'] text-3xl text-white uppercase text-center mb-8">
            Website Changes & Updates
          </h2>
          <div className="bg-[#0A0A0A] border border-[#262626] p-8 text-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div>
                <p className="font-['Oswald'] text-4xl text-[#FF4500]">4</p>
                <p className="font-['Manrope'] text-[#888888]">Free Changes Included</p>
              </div>
              <div className="text-[#888888] font-['Oswald'] text-2xl">THEN</div>
              <div>
                <p className="font-['Oswald'] text-4xl text-white">£20</p>
                <p className="font-['Manrope'] text-[#888888]">Per Hour (1hr minimum)</p>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
              {["Swapping images or photos", "Changing colours or fonts", "Adding new pages", "New features or integrations", "Updating text and wording", "Icon or logo updates", "Moving or restructuring sections", "Full redesigns (quoted separately)"].map((item, i) => (
                <p key={i} className="font-['Manrope'] text-[#888888] text-sm">• {item}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Extras & Add-ons */}
      <section className="py-16 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['Oswald'] text-3xl text-white uppercase text-center mb-4">
            Extras & Add-Ons
          </h2>
          <p className="font-['Manrope'] text-[#888888] text-center mb-8">
            Available on any package. Prices are fixed – no hourly rate is charged on top.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {extras.map((extra, i) => (
              <div key={i} className="flex items-center justify-between bg-[#050505] border border-[#262626] p-4">
                <span className="font-['Manrope'] text-[#CCCCCC]">{extra.name}</span>
                <span className="font-['Oswald'] text-[#FF4500] text-xl">£{extra.price}</span>
              </div>
            ))}
          </div>
          <p className="font-['Manrope'] text-[#888888] text-sm text-center mt-6">
            API integrations connect your site to tools like Xero, Stripe, Google Sheets, external booking systems, and more.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl text-white uppercase mb-6">
            Ready to Get Started?
          </h2>
          <p className="font-['Manrope'] text-[#888888] text-lg mb-8">
            Get in touch for a free consultation. First draft in as little as 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:george@wiltshirewebstudio.com">
              <Button className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider px-8 py-6">
                Email Me
              </Button>
            </a>
            <a href="tel:07767071806">
              <Button className="btn-secondary rounded-none font-['Oswald'] uppercase tracking-wider px-8 py-6">
                Call 07767 071806
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-['Manrope'] text-[#888888] text-sm">
            © 2026 Wiltshire Web Studio. All prices include VAT where applicable.
          </p>
          <Link to="/">
            <Button variant="ghost" className="text-[#888888] hover:text-white">
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
